// TODO: Migrate to backend REST — Firebase Storage operations need S3 presigned URL support first.
// Keeping Firebase Storage implementation for now.
import { firebaseApiWrapper } from "./firebaseApiWrapper.rtkq";
import {
  ref,
  uploadBytes,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
  listAll,
  getMetadata,
  updateMetadata,
  type StorageReference,
  type UploadMetadata,
  type FullMetadata,
} from "firebase/storage";
import {
  collection,
  doc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  deleteDoc,
  type Timestamp,
} from "firebase/firestore";
import { storage, db } from "@/lib/firebase";
import { Sentry } from "@/lib/sentry";

export interface UploadProgress {
  bytesTransferred: number;
  totalBytes: number;
  percentage: number;
  state: "running" | "paused" | "success" | "canceled" | "error";
}

export interface StorageFile {
  name: string;
  fullPath: string;
  downloadURL: string;
  metadata: FullMetadata;
  ref?: StorageReference;
}

export interface UploadFileParams {
  file: File;
  path: string;
  metadata?: UploadMetadata;
  userId: string;
}

export interface DeleteFileParams {
  path: string;
  userId: string;
}

export interface GetFileURLParams {
  path: string;
  userId: string;
}

export interface GetFileMetadataParams {
  path: string;
  userId: string;
}

export interface UpdateFileMetadataParams {
  path: string;
  newMetadata: UploadMetadata;
  userId: string;
}

export interface ListFilesParams {
  path?: string;
  userId: string;
}

export interface AudioFile {
  name: string;
  fullPath: string;
  downloadURL: string;
  metadata: {
    size: number;
    timeCreated: string;
    contentType: string;
  };
}

// PDF Extraction Document types
export interface PdfExtractionDocument {
  docId: string;
  originalPath: string;
  storageFileName: string;
  extractedContent: string;
  /**
   * Format of `extractedContent`. Documents written before the pivot to the
   * backend OCR pipeline carry no such field and hold HTML, so an absent value
   * MUST be read as 'html' — do not default it to the new format.
   */
  contentFormat?: 'html' | 'markdown';
  type: 'pdf_extraction';
  isScanned: boolean;
  processingStatus: 'completed' | 'failed' | 'pending';
  processingModel: string;
  pageCount: number;
  creditsConsumed: number;
  createdAt: Timestamp;
  updatedAt?: Timestamp;
  accountId: string;
  fileSize?: number;
  contentType?: string;
  extractionDuration?: number;
  /** Postgres case/file the extraction ran under, for tracing back into the backend. */
  caseId?: string;
  fileId?: string;
}

export interface GetPdfDocumentsParams {
  userId: string;
  limit?: number;
}

export interface DeletePdfDocumentParams {
  userId: string;
  docId: string;
  storagePath: string;
}

// Extends the existing apiSlice by injecting additional endpoints to communicate with Firebase Storage
export const documentsApi = firebaseApiWrapper.injectEndpoints({
  endpoints: (builder) => ({
    // File upload with progress tracking
    uploadFile: builder.mutation<{ downloadURL: string | null; error: Error | null }, UploadFileParams>({
      queryFn: async ({ file, path, metadata, userId }) => {
        try {
          const fileRef = ref(storage, `${userId}/${path}`);
          const transcriptionId = metadata?.customMetadata?.transcriptionId;
          const storagePath = `${userId}/${path}`;

          return await Sentry.startSpan(
            {
              name: "storage.upload",
              op: "file.upload",
              attributes: {
                "file.name": file.name,
                "file.size": file.size,
                "storage.path": storagePath,
                ...(transcriptionId ? { transcription_id: transcriptionId } : {}),
              },
            },
            async () => {
              if (transcriptionId) {
                Sentry.setTag("transcription_id", transcriptionId);
              }
              Sentry.setTag("pipeline", "recording_upload");
              Sentry.setTag("storage_path", storagePath);

              const uploadTask = uploadBytesResumable(fileRef, file, metadata);
              return new Promise<{ data: { downloadURL: string | null; error: null } } | { error: { message: string } }>((resolve) => {
                uploadTask.on(
                  "state_changed",
                  (snapshot) => {
                    const percentage =
                      snapshot.totalBytes > 0
                        ? Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)
                        : 0;
                    Sentry.addBreadcrumb({
                      category: "storage.upload",
                      message: `Upload progress ${percentage}%`,
                      level: "info",
                      data: {
                        bytesTransferred: snapshot.bytesTransferred,
                        totalBytes: snapshot.totalBytes,
                        state: snapshot.state,
                        transcriptionId,
                      },
                    });
                  },
                  (error) => {
                    Sentry.captureException(error);
                    resolve({ error: { message: (error as Error).message } });
                  },
                  async () => {
                    try {
                      const url = await getDownloadURL(uploadTask.snapshot.ref);
                      resolve({ data: { downloadURL: url, error: null } });
                    } catch (error: unknown) {
                      console.log("error", error);
                      Sentry.captureException(error);
                      resolve({ error: { message: (error as Error).message } });
                    }
                  }
                );
              });
            }
          );
        } catch (error: unknown) {
          return { error: { message: (error as Error).message } };
        }
      },
      invalidatesTags: ["Documents"],
    }),

    // Simple file upload without progress tracking
    uploadFileSimple: builder.mutation<{ downloadURL: string | null; error: Error | null }, UploadFileParams>({
      queryFn: async ({ file, path, metadata, userId }) => {
        try {
          const fileRef = ref(storage, `${userId}/${path}`);
          const snapshot = await uploadBytes(fileRef, file, metadata);
          const url = await getDownloadURL(snapshot.ref);
          return { data: { downloadURL: url, error: null } };
        } catch (error: unknown) {
          return { error: { message: (error as Error).message } };
        }
      },
      invalidatesTags: ["Documents"],
    }),

    // Delete file
    deleteFile: builder.mutation<{ error: Error | null }, DeleteFileParams>({
      queryFn: async ({ path, userId }) => {
        try {
          const fileRef = ref(storage, `${userId}/${path}`);
          await deleteObject(fileRef);
          return { data: { error: null } };
        } catch (error: unknown) {
          return { error: { message: (error as Error).message } };
        }
      },
      invalidatesTags: ["Documents"],
    }),

    // Get file download URL
    getFileURL: builder.query<{ downloadURL: string | null; error: Error | null }, GetFileURLParams>({
      queryFn: async ({ path, userId }) => {
        try {
          const fileRef = ref(storage, `${userId}/${path}`);
          const url = await getDownloadURL(fileRef);
          return { data: { downloadURL: url, error: null } };
        } catch (error: unknown) {
          return { error: { message: (error as Error).message } };
        }
      },
      providesTags: (result, _error, { path, userId }) => (result ? [{ type: "Documents", id: `${userId}/${path}` }] : []),
    }),

    // Get file metadata
    getFileMetadata: builder.query<{ metadata: FullMetadata | null; error: Error | null }, GetFileMetadataParams>({
      queryFn: async ({ path, userId }) => {
        try {
          const fileRef = ref(storage, `${userId}/${path}`);
          const metadata = await getMetadata(fileRef);
          return { data: { metadata, error: null } };
        } catch (error: unknown) {
          return { error: { message: (error as Error).message } };
        }
      },
      providesTags: (result, _error, { path, userId }) => (result ? [{ type: "Documents", id: `${userId}/${path}` }] : []),
    }),

    // Update file metadata
    updateFileMetadata: builder.mutation<{ metadata: FullMetadata | null; error: Error | null }, UpdateFileMetadataParams>({
      queryFn: async ({ path, newMetadata, userId }) => {
        try {
          const fileRef = ref(storage, `${userId}/${path}`);
          const metadata = await updateMetadata(fileRef, newMetadata);
          return { data: { metadata, error: null } };
        } catch (error: unknown) {
          return { error: { message: (error as Error).message } };
        }
      },
      invalidatesTags: (result, _error, { path, userId }) => (result ? [{ type: "Documents", id: `${userId}/${path}` }] : []),
    }),

    // List all files in a directory
    listFiles: builder.query<StorageFile[] | AudioFile[], ListFilesParams>({
      queryFn: async ({ path = "", userId }) => {
        try {
          const folderRef = ref(storage, `${userId}/${path}`);
          const result = await listAll(folderRef);

          const files: StorageFile[] = await Promise.all(
            result.items.map(async (itemRef) => {
              const [downloadURL, metadata] = await Promise.all([getDownloadURL(itemRef), getMetadata(itemRef)]);

              return {
                name: itemRef.name,
                fullPath: itemRef.fullPath,
                downloadURL,
                metadata,
                ref: itemRef,
              };
            })
          );

          return { data: files };
        } catch (err: unknown) {
          const error = err as Error;
          console.error("Storage list error:", error);

          // Provide more specific error messages
          if (error.message.includes("storage/unauthorized")) {
            const authError = new Error("You don't have permission to access these files. Please check your authentication.");
            return { error: { message: authError.message } };
          }

          if (error.message.includes("CORS")) {
            const corsError = new Error("Storage access blocked. Please deploy storage rules and try again.");
            return { error: { message: (corsError as Error).message } };
          }

          return { error: { message: (error as Error).message } };
        }
      },
      providesTags: (result, _error, { path, userId }) => (result ? [{ type: "Documents", id: `${userId}/${path}` }] : []),
    }),

    // PDF Documents - Get all PDF extraction documents
    getPdfDocuments: builder.query<PdfExtractionDocument[], GetPdfDocumentsParams>({
      queryFn: async ({ userId, limit: limitCount = 50 }) => {
        try {
          const documentsRef = collection(db, "users", userId, "documents");
          const q = query(
            documentsRef,
            where("type", "==", "pdf_extraction"),
            orderBy("createdAt", "desc"),
            limit(limitCount)
          );
          const snapshot = await getDocs(q);
          const documents = snapshot.docs.map((doc) => ({
            ...doc.data(),
            docId: doc.id,
          })) as PdfExtractionDocument[];
          return { data: documents };
        } catch (error: unknown) {
          const firestoreError = error as Error & { code?: string };
          console.error("Error fetching PDF documents:", {
            message: firestoreError.message,
            code: firestoreError.code,
            error: firestoreError,
          });

          // If it's an index error, try fallback query without orderBy
          if (firestoreError.message?.includes("index") || firestoreError.code === "failed-precondition") {
            console.warn(
              "Firestore index required! Falling back to query without orderBy. Create a composite index for 'type' and 'createdAt' fields in Firebase Console."
            );

            try {
              // Fallback: query without orderBy, sort in memory
              const documentsRef = collection(db, "users", userId, "documents");
              const fallbackQuery = query(
                documentsRef,
                where("type", "==", "pdf_extraction"),
                limit(limitCount * 2) // Get more to account for sorting
              );
              const snapshot = await getDocs(fallbackQuery);
              const allDocuments = snapshot.docs.map((doc) => ({
                ...doc.data(),
                docId: doc.id,
              })) as PdfExtractionDocument[];

              const documents = allDocuments
                .sort((a, b) => {
                  // Sort by createdAt descending in memory
                  const getTimestamp = (ts: Timestamp | undefined): number => {
                    if (!ts) return 0;
                    if (typeof ts.toMillis === "function") {
                      return ts.toMillis();
                    }
                    const seconds = (ts as { seconds?: number }).seconds;
                    return seconds ? seconds * 1000 : 0;
                  };
                  const aTime = getTimestamp(a.createdAt as Timestamp | undefined);
                  const bTime = getTimestamp(b.createdAt as Timestamp | undefined);
                  return bTime - aTime;
                })
                .slice(0, limitCount);

              return { data: documents };
            } catch (fallbackError: unknown) {
              console.error("Fallback query also failed:", fallbackError);
              return { error: { message: (fallbackError as Error).message } };
            }
          }

          return { error: { message: firestoreError.message } };
        }
      },
      providesTags: (result) =>
        result ? [...result.map((doc) => ({ type: "Documents" as const, id: doc.docId })), "Documents"] : ["Documents"],
    }),

    // PDF Documents - Delete a PDF extraction document
    deletePdfDocument: builder.mutation<void, DeletePdfDocumentParams>({
      queryFn: async ({ userId, docId, storagePath }) => {
        try {
          // Delete from Firestore
          await deleteDoc(doc(db, "users", userId, "documents", docId));

          // Delete from Storage
          const storageRef = ref(storage, storagePath);
          await deleteObject(storageRef);

          return { data: undefined };
        } catch (error: unknown) {
          return { error: { message: (error as Error).message } };
        }
      },
      invalidatesTags: (_result, _error, { docId }) => [
        { type: "Documents", id: docId },
        "Documents",
      ],
    }),
  }),
});

export const {
  // Storage operations
  useUploadFileMutation,
  useUploadFileSimpleMutation,
  useDeleteFileMutation,
  useGetFileURLQuery,
  useLazyGetFileURLQuery,
  useGetFileMetadataQuery,
  useUpdateFileMetadataMutation,
  useListFilesQuery,
  // PDF Documents operations
  useGetPdfDocumentsQuery,
  useDeletePdfDocumentMutation,
} = documentsApi;
